import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { postRemito } from "../helpers/rutaRemitos";
import { getProveedores } from "../helpers/rutaProveedores";
import { getClientes } from "../helpers/rutaClientes";
import { getTransportes } from "../helpers/rutaTransportes";
import { getChoferes } from "../helpers/rutaChoferes";
import { getLotes } from "../helpers/rutaLotes";

const sortBy = (arr, key) =>
  [...(arr || [])].sort((a, b) =>
    (a?.[key] || "").localeCompare(b?.[key] || "", "es", { sensitivity: "base" })
  );

const VARIEDADES_SUGERIDAS = ["ATLANTIC", "INNOVATOR", "SPUNTA", "BATATA"];

const makeInitialForm = () => ({
  fecha: new Date().toISOString().slice(0, 10),
  kgCampo: "",
  kgRecibido: "",
  precioCompra: "",
  precioVenta: "",
  proveedor: "",
  cliente: "",
  transporte: "",
  chofer: "",
  lote: "",
  variedad: "",
  nroDTVCompra: "",
  nroDTVVenta: "",
  nota: "",
  factura: false,

  porcentajeCompra: 100, // % en negro compra
  porcentajeVenta: 100,  // % en negro venta
  flete: 0,
  facturacompra: "",
  facturaventa: "",

  // ✅ Manuales (vacío => automático por %)
  compraAManual: "",
  compraBManual: "",
  ventaAManual: "",
  ventaBManual: "",

  // ✅ Flags (para saber si el valor fue auto-completado)
  autoCompraA: false,
  autoCompraB: false,
  autoVentaA: false,
  autoVentaB: false,
});

function parseNumber(value, def = 0) {
  if (value === "" || value === null || value === undefined) return def;
  const n = Number(value);
  return Number.isNaN(n) ? def : n;
}

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const to2 = (n) => (Number.isFinite(n) ? Number(n).toFixed(2) : "0.00");

const AddRemitoForm = ({ show, onClose }) => {
  const [okModal, setOkModal] = useState(false);
  const [form, setForm] = useState(makeInitialForm());

  const [lists, setLists] = useState({
    prov: [],
    cli: [],
    tra: [],
    chof: [],
    lot: [],
    loading: true,
    error: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const [prov, cli, tra, chof, lot] = await Promise.all([
          getProveedores(),
          getClientes(),
          getTransportes(),
          getChoferes(),
          getLotes(),
        ]);

        setLists({
          prov: Array.isArray(prov) ? prov : prov?.proveedores || [],
          cli: Array.isArray(cli) ? cli : cli?.clientes || [],
          tra: Array.isArray(tra) ? tra : tra?.transportes || [],
          chof: Array.isArray(chof) ? chof : chof?.choferes || [],
          lot: Array.isArray(lot) ? lot : lot?.lotes || [],
          loading: false,
          error: "",
        });
      } catch (e) {
        setLists((s) => ({
          ...s,
          loading: false,
          error: e?.message || "Error al cargar listas",
        }));
      }
    })();
  }, []);

  const provOpts = useMemo(() => sortBy(lists.prov, "razonsocial"), [lists.prov]);
  const cliOpts = useMemo(() => sortBy(lists.cli, "razonsocial"), [lists.cli]);
  const traOpts = useMemo(() => sortBy(lists.tra, "transporte"), [lists.tra]);
  const chofOpts = useMemo(() => sortBy(lists.chof, "nombre"), [lists.chof]);
  const lotOpts = useMemo(() => sortBy(lists.lot, "lote"), [lists.lot]);

  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // 🔢 cálculos base
  const kgRecibidoNum = Number(form.kgRecibido || 0);
  const kgCampoNum = Number(form.kgCampo || 0);
  const diferencia = kgCampoNum - kgRecibidoNum;

  const precioCompraNum = Number(form.precioCompra || 0);
  const precioVentaNum = Number(form.precioVenta || 0);

  const totalCompra = kgRecibidoNum * precioCompraNum;
  const totalVenta = kgRecibidoNum * precioVentaNum;
  const comision = totalVenta - totalCompra;

  const IVA_105 = 0.105;

  const pctCompra = Number(form.porcentajeCompra || 0);
  const pctVenta = Number(form.porcentajeVenta || 0);

  const fracCompraNegro = Math.min(Math.max(pctCompra / 100, 0), 1);
  const fracVentaNegro = Math.min(Math.max(pctVenta / 100, 0), 1);
  const fracCompraBlanco = 1 - fracCompraNegro;
  const fracVentaBlanco = 1 - fracVentaNegro;

  // ✅ automáticos por %
  const compraB_calc = kgRecibidoNum * precioCompraNum * fracCompraNegro;
  const compraA_calc = kgRecibidoNum * precioCompraNum * (1 + IVA_105) * fracCompraBlanco;

  const ventaB_calc = kgRecibidoNum * precioVentaNum * fracVentaNegro;
  const ventaA_calc = kgRecibidoNum * precioVentaNum * (1 + IVA_105) * fracVentaBlanco;

  // ✅ bases sin IVA para complementar manual
  const baseCompra = Math.max(0, kgRecibidoNum * precioCompraNum); // sin IVA
  const baseVenta = Math.max(0, kgRecibidoNum * precioVentaNum);   // sin IVA

  const calcCompraAFromB = (b) => (baseCompra - clamp(b, 0, baseCompra)) * (1 + IVA_105);
  const calcCompraBFromA = (a) => baseCompra - clamp(a / (1 + IVA_105), 0, baseCompra);

  const calcVentaAFromB = (b) => (baseVenta - clamp(b, 0, baseVenta)) * (1 + IVA_105);
  const calcVentaBFromA = (a) => baseVenta - clamp(a / (1 + IVA_105), 0, baseVenta);

  // ✅ handlers "anti-dedos"
  const handleCompraBManual = (e) => {
    const v = e.target.value;
    setForm((prev) => {
      const next = { ...prev, compraBManual: v, autoCompraB: false };
      const bNum = v === "" ? 0 : Number(v);

      if (!Number.isNaN(bNum) && (prev.compraAManual === "" || prev.autoCompraA)) {
        next.compraAManual = v === "" ? "" : to2(calcCompraAFromB(bNum));
        next.autoCompraA = v !== "";
      }
      return next;
    });
  };

  const handleCompraAManual = (e) => {
    const v = e.target.value;
    setForm((prev) => {
      const next = { ...prev, compraAManual: v, autoCompraA: false };
      const aNum = v === "" ? 0 : Number(v);

      if (!Number.isNaN(aNum) && (prev.compraBManual === "" || prev.autoCompraB)) {
        next.compraBManual = v === "" ? "" : to2(calcCompraBFromA(aNum));
        next.autoCompraB = v !== "";
      }
      return next;
    });
  };

  const handleVentaBManual = (e) => {
    const v = e.target.value;
    setForm((prev) => {
      const next = { ...prev, ventaBManual: v, autoVentaB: false };
      const bNum = v === "" ? 0 : Number(v);

      if (!Number.isNaN(bNum) && (prev.ventaAManual === "" || prev.autoVentaA)) {
        next.ventaAManual = v === "" ? "" : to2(calcVentaAFromB(bNum));
        next.autoVentaA = v !== "";
      }
      return next;
    });
  };

  const handleVentaAManual = (e) => {
    const v = e.target.value;
    setForm((prev) => {
      const next = { ...prev, ventaAManual: v, autoVentaA: false };
      const aNum = v === "" ? 0 : Number(v);

      if (!Number.isNaN(aNum) && (prev.ventaBManual === "" || prev.autoVentaB)) {
        next.ventaBManual = v === "" ? "" : to2(calcVentaBFromA(aNum));
        next.autoVentaB = v !== "";
      }
      return next;
    });
  };

  // ✅ finales: manual si hay, sino automático
  const compraA_final = form.compraAManual !== "" ? parseNumber(form.compraAManual) : compraA_calc;
  const compraB_final = form.compraBManual !== "" ? parseNumber(form.compraBManual) : compraB_calc;
  const ventaA_final = form.ventaAManual !== "" ? parseNumber(form.ventaAManual) : ventaA_calc;
  const ventaB_final = form.ventaBManual !== "" ? parseNumber(form.ventaBManual) : ventaB_calc;

  const tituloCompra = { color: "#0d6efd", fontWeight: 700 };
  const tituloVenta = { color: "#dc3545", fontWeight: 700 };

  const fieldStyle = (manualValue, isAuto) => ({
    backgroundColor: manualValue === "" ? "#f1f3f5" : isAuto ? "#eef2f7" : "#ffffff",
  });

  useEffect(() => {
    if (show) setForm(makeInitialForm());
  }, [show]);

  const submit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        fecha: form.fecha,

        kgCampo: parseNumber(form.kgCampo),
        kgRecibido: parseNumber(form.kgRecibido),
        precioCompra: parseNumber(form.precioCompra),
        precioVenta: parseNumber(form.precioVenta),

        proveedor: form.proveedor || null,
        cliente: form.cliente || null,
        transporte: form.transporte || null,
        chofer: form.chofer || null,
        lote: form.lote || null,

        variedad: (form.variedad || "").trim(),
        nroDTVCompra: (form.nroDTVCompra || "").trim(),
        nroDTVVenta: (form.nroDTVVenta || "").trim(),

        facturacompra: (form.facturacompra || "").trim(),
        facturaventa: (form.facturaventa || "").trim(),

        nota: form.nota || "",
        factura: !!form.factura,

        porcentajeCompra: parseNumber(form.porcentajeCompra),
        porcentajeVenta: parseNumber(form.porcentajeVenta),
        flete: parseNumber(form.flete),

        // ✅ Guardamos A/B finales (manual o automático)
        compraA: compraA_final,
        compraB: compraB_final,
        ventaA: ventaA_final,
        ventaB: ventaB_final,
      };

      const resp = await postRemito(payload);
      if (resp?.ok === false) throw { response: { data: resp } };

      setForm(makeInitialForm());
      setOkModal(true);
      setTimeout(() => setOkModal(false), 800);
    } catch (error) {
      const d = error?.response?.data;
      const msg =
        d?.err?.message || d?.error?.message || d?.message || "No se pudo crear el remito.";
      alert(msg);
    }
  };

  if (!show) return null;

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg">
        <form onSubmit={submit}>
          <Modal.Header closeButton>
            <Modal.Title>Nuevo Remito</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label>Fecha</label>
                <input type="date" name="fecha" className="form-control" value={form.fecha} onChange={handle} required />
              </div>

              <div className="col-md-3 mb-3">
                <label>KG Campo</label>
                <input type="number" name="kgCampo" className="form-control" value={form.kgCampo} onChange={handle} min="0" step="1" required />
              </div>

              <div className="col-md-3 mb-3">
                <label>KG Recibidos</label>
                <input type="number" name="kgRecibido" className="form-control" value={form.kgRecibido} onChange={handle} min="0" step="1" required />
              </div>

              <div className="col-md-3 mb-3">
                <label>Diferencia</label>
                <input className="form-control" value={diferencia} readOnly />
              </div>

              <div className="col-md-3 mb-3">
                <label>Precio Compra ($/kg)</label>
                <input type="number" name="precioCompra" className="form-control" value={form.precioCompra} onChange={handle} min="0" step="0.01" required />
              </div>

              <div className="col-md-3 mb-3">
                <label>Precio Venta ($/kg)</label>
                <input type="number" name="precioVenta" className="form-control" value={form.precioVenta} onChange={handle} min="0" step="0.01" required />
              </div>

              <div className="col-md-2 mb-3">
                <label>Total Compra</label>
                <input className="form-control" value={totalCompra.toLocaleString("es-AR", { minimumFractionDigits: 2 })} readOnly />
              </div>

              <div className="col-md-2 mb-3">
                <label>Total Venta</label>
                <input className="form-control" value={totalVenta.toLocaleString("es-AR", { minimumFractionDigits: 2 })} readOnly />
              </div>

              <div className="col-md-2 mb-3">
                <label>Comisión</label>
                <input className="form-control" value={comision.toLocaleString("es-AR", { minimumFractionDigits: 2 })} readOnly />
              </div>

              <div className="col-md-4 mb-3">
                <label>Proveedor</label>
                <select className="form-control" name="proveedor" value={form.proveedor} onChange={handle} required>
                  <option value="">Elija opción</option>
                  {provOpts.map((p) => (
                    <option key={p._id} value={p._id}>{p.razonsocial}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>Cliente</label>
                <select className="form-control" name="cliente" value={form.cliente} onChange={handle} required>
                  <option value="">Elija opción</option>
                  {cliOpts.map((c) => (
                    <option key={c._id} value={c._id}>{c.razonsocial}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>Transporte</label>
                <select className="form-control" name="transporte" value={form.transporte} onChange={handle} required>
                  <option value="">Elija opción</option>
                  {traOpts.map((t) => (
                    <option key={t._id} value={t._id}>{t.transporte}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>Chofer</label>
                <select className="form-control" name="chofer" value={form.chofer} onChange={handle}>
                  <option value="">Elija opción</option>
                  {chofOpts.map((ch) => (
                    <option key={ch._id} value={ch._id}>{ch.nombre}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>Lote</label>
                <select className="form-control" name="lote" value={form.lote} onChange={handle}>
                  <option value="">Elija opción</option>
                  {lotOpts.map((l) => (
                    <option key={l._id} value={l._id}>{l.lote}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label>Variedad</label>
                <input
                  className="form-control"
                  name="variedad"
                  value={form.variedad}
                  onChange={handle}
                  list="variedades-sugeridas"
                  placeholder="Escriba o elija una variedad"
                />
                <datalist id="variedades-sugeridas">
                  {VARIEDADES_SUGERIDAS.map((v) => (
                    <option key={v} value={v} />
                  ))}
                </datalist>
              </div>

              <div className="form-row">
                <div className="col-md-2 mb-3">
                  <label style={tituloCompra}>Nro DTV Compra</label>
                  <input type="text" className="form-control" name="nroDTVCompra" value={form.nroDTVCompra} onChange={handle} />
                </div>

                <div className="col-md-2 mb-3">
                  <label style={tituloCompra}>% Compra B</label>
                  <input type="number" className="form-control" name="porcentajeCompra" min="0" max="100" step="1" value={form.porcentajeCompra} onChange={handle} />
                </div>

                <div className="col-md-2 mb-3">
                  <label style={tituloCompra}>Factura Compra</label>
                  <input type="text" className="form-control" name="facturacompra" value={form.facturacompra} onChange={handle} />
                </div>

                <div className="col-md-2 mb-3">
                  <label style={tituloVenta}>Nro DTV Venta</label>
                  <input type="text" className="form-control" name="nroDTVVenta" value={form.nroDTVVenta} onChange={handle} />
                </div>

                <div className="col-md-2 mb-3">
                  <label style={tituloVenta}>% Venta B</label>
                  <input type="number" className="form-control" name="porcentajeVenta" min="0" max="100" step="1" value={form.porcentajeVenta} onChange={handle} />
                </div>

                <div className="col-md-2 mb-3">
                  <label style={tituloVenta}>Factura Venta</label>
                  <input type="text" className="form-control" name="facturaventa" value={form.facturaventa} onChange={handle} />
                </div>

                <div className="col-md-2 mb-3">
                  <label>Flete (Monto)</label>
                  <input type="number" className="form-control" name="flete" step="0.01" value={form.flete} onChange={handle} />
                </div>
              </div>

              {/* ✅ A/B EDITABLES + placeholders automáticos */}
              <div className="col-md-3 mb-3">
                <label>Compra A (blanco + IVA)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={form.compraAManual}
                  onChange={handleCompraAManual}
                  placeholder={to2(compraA_calc)}
                  style={fieldStyle(form.compraAManual, form.autoCompraA)}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>Compra B (negro)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={form.compraBManual}
                  onChange={handleCompraBManual}
                  placeholder={to2(compraB_calc)}
                  style={fieldStyle(form.compraBManual, form.autoCompraB)}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>Venta A (blanco + IVA)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={form.ventaAManual}
                  onChange={handleVentaAManual}
                  placeholder={to2(ventaA_calc)}
                  style={fieldStyle(form.ventaAManual, form.autoVentaA)}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label>Venta B (negro)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  value={form.ventaBManual}
                  onChange={handleVentaBManual}
                  placeholder={to2(ventaB_calc)}
                  style={fieldStyle(form.ventaBManual, form.autoVentaB)}
                />
              </div>

              <div className="col-md-12 mb-3">
                <label>Notas</label>
                <textarea className="form-control" rows={2} name="nota" value={form.nota} onChange={handle} />
              </div>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>Cancelar</Button>
            <Button variant="dark" type="submit">Guardar</Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={okModal} centered backdrop="static" keyboard={false}>
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>✅</div>
          <h5 className="mt-2">¡Remito grabado!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddRemitoForm;
